/**
 * EnterpriseEntityPicker — Sprint A1 (22 Feb 2026).
 *
 * DE universele zoek- en selectiedialoog voor het volledige platform.
 * Vervangt CustomerSearchField / BookingSearchField / InvoiceSearchField et al.
 *
 * Belangrijkste eigenschappen:
 * ─────────────────────────────
 * - **Config-driven** — gebruikt `entityRegistry` + backend `/entity-search`
 *   registry. Nieuwe entiteiten toevoegen = 1 adapter registreren, klaar.
 * - **Twee modes** — `inline` (dropdown onder input, ideaal voor forms) en
 *   `dialog` (modal popup, ideaal voor stand-alone "kies een klant").
 * - **Full keyboard nav** — ↑↓ door resultaten, Enter selecteert, Escape sluit,
 *   Tab focus-management.
 * - **Recente selecties** — persistent per user via user_preferences. Toont
 *   direct wanneer input leeg is (nul-typ suggesties).
 * - **Debounce 300ms + min 2 chars** — uniform gedrag met andere zoekers.
 * - **Loading / empty / error states** — allemaal expliciet.
 * - **Rijke result-cards** — icon/avatar + primary/secondary + badges + status.
 *
 * Basic gebruik (inline):
 *   <EnterpriseEntityPicker
 *     entityType="customer"
 *     onSelect={(c) => setSelectedCustomer(c)}
 *     testIdPrefix="regeling"
 *   />
 *
 * Multi-entity gebruik (Command Palette-achtig):
 *   <EnterpriseEntityPicker
 *     entityTypes={["customer", "invoice", "booking"]}
 *     mode="dialog"
 *     onSelect={(item) => handleSelection(item)}
 *   />
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import { Search, Loader2, X, Clock, ArrowRight, Star } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Input } from "../../ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../ui/dialog";
import { getEntityConfig } from "./entityRegistry";

// ─── Constants ────────────────────────────────────────────────
const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
const RESULT_LIMIT = 8;

// ─── Badge tone → tailwind classes ────────────────────────────
const BADGE_TONES = {
  success: "bg-emerald-100 text-emerald-800 border-emerald-200",
  info: "bg-blue-100 text-blue-800 border-blue-200",
  warning: "bg-amber-100 text-amber-800 border-amber-200",
  danger: "bg-red-100 text-red-800 border-red-200",
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  muted: "bg-slate-50 text-slate-500 border-slate-200",
};

// ─── Result card — reusable in inline & dialog modes ──────────
function ResultCard({ item, isActive, config, onClick, testId, isFavorite, onToggleFavorite }) {
  const Icon = config.icon;
  return (
    <div
      role="option"
      aria-selected={isActive}
      className={`w-full text-left px-3 py-2.5 flex items-center gap-3 border-b border-slate-100 last:border-b-0 transition-colors ${
        isActive ? "bg-blue-50 border-l-2 border-l-blue-600" : "hover:bg-slate-50"
      }`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex-1 min-w-0 flex items-center gap-3 text-left focus:outline-none"
        data-testid={`${testId}-select`}
      >
      {item.avatar_text ? (
        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0">
          {item.avatar_text}
        </div>
      ) : (
        <div className="w-8 h-8 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="font-medium text-sm text-slate-900 truncate">
            {item.primary_text}
          </span>
          {(item.badges || []).map((b, i) => (
            <span
              key={i}
              className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                BADGE_TONES[b.tone] || BADGE_TONES.neutral
              }`}
            >
              {b.label}
            </span>
          ))}
        </div>
        {item.secondary_text && (
          <div className="text-xs text-slate-500 truncate">
            {item.secondary_text}
          </div>
        )}
        {item.tertiary_text && (
          <div className="text-[11px] text-slate-400 truncate">
            {item.tertiary_text}
          </div>
        )}
      </div>
      {isActive && (
        <ArrowRight className="w-4 h-4 text-blue-600 flex-shrink-0" />
      )}
      </button>
      {onToggleFavorite && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(item); }}
          className={`ml-1 p-1 shrink-0 rounded transition-colors ${
            isFavorite
              ? "text-amber-500 hover:text-amber-600"
              : "text-slate-300 hover:text-amber-400"
          }`}
          aria-label={isFavorite ? "Verwijder uit favorieten" : "Voeg toe aan favorieten"}
          data-testid={`${testId}-favorite-toggle`}
        >
          <Star className={`w-3.5 h-3.5 ${isFavorite ? "fill-current" : ""}`} />
        </button>
      )}
    </div>
  );
}

// ─── Shared search state hook ─────────────────────────────────
function useEntitySearch({ entityTypes, initialQuery = "" }) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    if (!query || query.trim().length < MIN_CHARS) {
      setResults([]);
      setLoading(false);
      return () => {
        cancelRef.current = true;
      };
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await adminApi.post("/admin/entity-search", {
          entity_types: entityTypes,
          q: query.trim(),
          limit: RESULT_LIMIT,
        });
        if (cancelRef.current) return;
        // Flatten results in preserving entity-type order
        const flat = [];
        entityTypes.forEach((et) => {
          (r.data?.results?.[et] || []).forEach((item) => flat.push(item));
        });
        setResults(flat);
        setError(null);
      } catch (e) {
        if (cancelRef.current) return;
        setResults([]);
        setError(formatApiError(e));
      } finally {
        if (!cancelRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelRef.current = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, JSON.stringify(entityTypes)]);

  return { query, setQuery, results, loading, error, setResults };
}

// ─── Recent selections hook ────────────────────────────────────
function useRecentSelections(entityType, isOpen) {
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    if (!entityType || !isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get(
          `/admin/user-preferences/recent-entity-selections/${entityType}`
        );
        if (!cancelled) setRecent(r.data?.items || []);
      } catch {
        // ignore — recent is nice-to-have
      }
    })();
    return () => { cancelled = true; };
  }, [entityType, isOpen]);
  return recent;
}

// ─── Favorites hook — Sprint A Finalisatie ────────────────────
function useEntityFavorites(entityType) {
  const [favIds, setFavIds] = useState(new Set());
  useEffect(() => {
    if (!entityType) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get("/admin/user-preferences");
        const favs = (r.data?.favorites || [])
          .filter((k) => typeof k === "string" && k.startsWith(`entity:${entityType}:`))
          .map((k) => k.split(":")[2])
          .filter(Boolean);
        if (!cancelled) setFavIds(new Set(favs));
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [entityType]);

  const toggle = useCallback(async (item) => {
    const key = `entity:${item.entity_type}:${item.id}`;
    try {
      const r = await adminApi.post("/admin/user-preferences/favorites/toggle", { key });
      const favs = new Set(
        (r.data?.favorites || [])
          .filter((k) => typeof k === "string" && k.startsWith(`entity:${item.entity_type}:`))
          .map((k) => k.split(":")[2])
          .filter(Boolean)
      );
      setFavIds(favs);
    } catch { /* silent */ }
  }, []);

  return { favIds, toggle };
}

// ─── Persist selection helper ─────────────────────────────────
async function persistSelection(item) {
  try {
    await adminApi.post("/admin/user-preferences/recent-entity-selections/add", {
      selection: {
        entity_type: item.entity_type,
        entity_id: item.id,
        primary_text: item.primary_text,
        secondary_text: item.secondary_text || null,
        icon: item.icon || null,
        avatar_text: item.avatar_text || null,
      },
    });
  } catch {
    // silent — persistence is nice-to-have
  }
}

// ─── Main component ──────────────────────────────────────────
export default function EnterpriseEntityPicker({
  // Single entity mode (bijv "customer") — of array voor multi-entity
  entityType,
  entityTypes,
  // Selection callback
  onSelect,
  // "inline" (default) of "dialog"
  mode = "inline",
  // Dialog-mode controls
  open,
  onOpenChange,
  // Ander customization
  placeholder,
  autoFocus = true,
  showRecent = true,
  testIdPrefix = "entity-picker",
  emptyLabel,
  className = "",
  // Extra rendering: hide trigger als je alleen de picker-panel wilt
  renderTrigger,
}) {
  const finalEntityTypes = useMemo(
    () => (entityTypes && entityTypes.length ? entityTypes : [entityType]),
    [entityType, entityTypes]
  );
  const primaryConfig = getEntityConfig(finalEntityTypes[0]);
  const singleType = finalEntityTypes.length === 1 ? finalEntityTypes[0] : null;

  const [internalOpen, setInternalOpen] = useState(false);
  const isDialogOpen = mode === "dialog" ? (open ?? internalOpen) : true;
  const setDialogOpen = mode === "dialog"
    ? (v) => (onOpenChange ? onOpenChange(v) : setInternalOpen(v))
    : () => {};

  const { query, setQuery, results, loading, error } = useEntitySearch({
    entityTypes: finalEntityTypes,
    initialQuery: "",
  });

  const recent = useRecentSelections(showRecent ? singleType : null, isDialogOpen);
  const { favIds, toggle: toggleFavorite } = useEntityFavorites(singleType);
  const [activeIdx, setActiveIdx] = useState(-1);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const displayItems = useMemo(() => {
    if (query.trim().length >= MIN_CHARS) return results;
    // Toon recente items als "recent" flag
    return recent.map((r) => ({
      id: r.entity_id,
      entity_type: r.entity_type,
      primary_text: r.primary_text,
      secondary_text: r.secondary_text,
      icon: r.icon,
      avatar_text: r.avatar_text,
      badges: [],
      _isRecent: true,
    }));
  }, [query, results, recent]);

  const handleSelect = useCallback(
    (item) => {
      if (!item) return;
      if (!item._isRecent) {
        persistSelection(item);
      }
      onSelect && onSelect(item);
      if (mode === "dialog") setDialogOpen(false);
      setQuery("");
      setActiveIdx(-1);
    },
    [onSelect, mode, setDialogOpen, setQuery]
  );

  // Reset active index when items change
  useEffect(() => {
    setActiveIdx(displayItems.length > 0 ? 0 : -1);
  }, [displayItems.length]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIdx < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeIdx]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, displayItems.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIdx >= 0 && displayItems[activeIdx]) {
          handleSelect(displayItems[activeIdx]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (mode === "dialog") setDialogOpen(false);
        else setQuery("");
      }
    },
    [activeIdx, displayItems, handleSelect, mode, setDialogOpen, setQuery]
  );

  // Panel body — gedeeld tussen inline + dialog
  const panelBody = (
    <>
      {/* Search input */}
      <div className={`flex items-center gap-2 ${mode === "dialog" ? "px-4 py-3 border-b border-slate-100" : ""}`}>
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || primaryConfig.placeholder}
            className={`pl-8 ${mode === "dialog" ? "text-base h-11 border-0 shadow-none focus-visible:ring-0" : "text-sm h-9"}`}
            autoComplete="off"
            autoFocus={autoFocus}
            data-testid={`${testIdPrefix}-search`}
            aria-controls={`${testIdPrefix}-results`}
            aria-activedescendant={
              activeIdx >= 0 ? `${testIdPrefix}-result-${activeIdx}` : undefined
            }
          />
          {loading && (
            <Loader2
              className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin"
              data-testid={`${testIdPrefix}-loading`}
            />
          )}
        </div>
        {mode === "dialog" && (
          <button
            type="button"
            onClick={() => setDialogOpen(false)}
            className="p-1.5 rounded hover:bg-slate-100 transition-colors"
            aria-label="Sluiten"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        )}
      </div>

      {/* Results / recent / empty / error */}
      <div
        ref={listRef}
        role="listbox"
        id={`${testIdPrefix}-results`}
        data-testid={`${testIdPrefix}-results`}
        className={`overflow-y-auto ${mode === "dialog" ? "max-h-96" : "max-h-72"} ${mode === "inline" ? "border border-slate-200 rounded-md bg-white shadow-sm" : ""}`}
      >
        {error && (
          <div className="px-3 py-2.5 text-xs text-red-700 bg-red-50 border-b border-red-100"
               data-testid={`${testIdPrefix}-error`}>
            {error}
          </div>
        )}

        {query.trim().length < MIN_CHARS && showRecent && recent.length > 0 && (
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50/50 flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            Recent geselecteerd
          </div>
        )}

        {displayItems.length === 0 && !loading && query.trim().length >= MIN_CHARS && (
          <div className="px-3 py-6 text-sm text-slate-500 italic text-center"
               data-testid={`${testIdPrefix}-empty`}>
            {emptyLabel || primaryConfig.emptyLabel}
          </div>
        )}

        {displayItems.length === 0 && !loading && query.trim().length < MIN_CHARS && !recent.length && (
          <div className="px-3 py-6 text-sm text-slate-400 italic text-center"
               data-testid={`${testIdPrefix}-hint`}>
            Typ minstens {MIN_CHARS} tekens om te zoeken…
          </div>
        )}

        {displayItems.map((item, idx) => {
          const cfg = getEntityConfig(item.entity_type);
          const isFav = singleType && favIds.has(item.id);
          return (
            <div key={`${item.entity_type}-${item.id}-${idx}`} data-idx={idx}
                 id={`${testIdPrefix}-result-${idx}`}>
              <ResultCard
                item={item}
                isActive={idx === activeIdx}
                config={cfg}
                onClick={() => handleSelect(item)}
                testId={`${testIdPrefix}-option-${item.id}`}
                isFavorite={isFav}
                onToggleFavorite={singleType ? toggleFavorite : undefined}
              />
            </div>
          );
        })}
      </div>
    </>
  );

  // ─── Inline mode ────────────────────────
  if (mode === "inline") {
    return (
      <div className={`space-y-1 ${className}`} data-testid={`${testIdPrefix}-inline`}>
        {panelBody}
      </div>
    );
  }

  // ─── Dialog mode ────────────────────────
  return (
    <>
      {renderTrigger && renderTrigger({ open: () => setDialogOpen(true) })}
      <Dialog open={isDialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-2xl w-[95vw] p-0 overflow-hidden rounded-xl shadow-2xl"
          data-testid={`${testIdPrefix}-dialog`}
        >
          <DialogTitle className="sr-only">{primaryConfig.label} zoeken</DialogTitle>
          <DialogDescription className="sr-only">
            Zoek en selecteer {primaryConfig.label.toLowerCase()} via de Enterprise Entity Picker.
          </DialogDescription>
          {panelBody}
          <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500 flex items-center justify-between">
            <span>
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px]">↑↓</kbd> navigeren
              &nbsp;
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px]">Enter</kbd> selecteren
              &nbsp;
              <kbd className="px-1.5 py-0.5 bg-white border border-slate-200 rounded font-mono text-[10px]">Esc</kbd> sluiten
            </span>
            <span className="text-slate-400">
              {loading ? "Zoeken…" : `${displayItems.length} resultaten`}
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
